import type { IAutoEmbedConfig } from '@expPlatform/embeds/integrations/shopify/types'

import once from '@core/lodash/once'
import { defaultApiHost } from '@src/helpers'

const API_HOST = defaultApiHost

/**
 * Get the store URL
 * @returns {string} - The store URL
 */
export function getStoreUrl(): string {
  try {
    return window.Shopify?.shop ?? ''
  } catch (error) {
    console.error('Error getting store URL', error)
  }
  return ''
}

export type ShopifyPageType =
  | 'shopify_standard_collection_page'
  | 'shopify_standard_homepage'
  | 'shopify_standard_product_page'
  | null

/**
 * Get the page type
 * @returns {string} - The page type
 * If the page is the homepage, return 'shopify_standard_homepage'
 * If the page is a product page, return 'shopify_standard_product_page'
 * If the page is a collection page, return 'shopify_standard_collection_page'
 * If the page is not supported, return null
 */
export function getPageType(): ShopifyPageType {
  const type = window.meta.page.pageType
  if (type === 'collection') {
    return 'shopify_standard_collection_page'
  } else if (type === 'home') {
    return 'shopify_standard_homepage'
  } else if (type === 'product') {
    return 'shopify_standard_product_page'
  } else {
    return null
  }
}

/**
 * Get the embed path
 * @param pageType - The page type
 * @returns {string} - The embed path
 */
export function getEmbedPath(pageType: ShopifyPageType): string | null {
  switch (pageType) {
    case 'shopify_standard_homepage':
      return '/'
    case 'shopify_standard_product_page':
      return '/products/'
    case 'shopify_standard_collection_page':
      return '/collections/'
    default:
      return null
  }
}

/**
 * check if fw_preview param is true
 * @returns boolean
 */
export function isPreviewMode(): boolean {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('fw_preview') === 'true'
}

async function fetchEmbedDetails(
  pageType: ShopifyPageType,
): Promise<IAutoEmbedConfig[] | void> {
  const path = getEmbedPath(pageType)
  if (!path) {
    return
  }
  const queryParams = new URLSearchParams({
    path,
    authority: getStoreUrl(),
    page_type: pageType ?? '',
    preview: isPreviewMode().toString(),
  })
  try {
    const response = await fetch(
      `${API_HOST}/embed/v2/embedded_components?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
    return await response.json()
  } catch (error) {
    return console.error('There was a problem fetching embed config', error)
  }
}

function createErrorDialogElement(): HTMLDivElement {
  const errorDialog = document.createElement('dialog')
  errorDialog.setAttribute('open', 'true')

  const heading = document.createElement('h1')
  heading.textContent = 'Section Position Not Found'

  const message = document.createElement('p')
  message.textContent =
    'We could not identify the section position selected in the embed settings. Please decrease the section position and try again.'

  const closeButton = document.createElement('button')
  closeButton.type = 'button'
  closeButton.textContent = 'Close'
  closeButton.style.cssText =
    'margin-top: 10px; padding: 5px 10px; background-color: #000; color: #fff; border: none; cursor: pointer;'
  closeButton.addEventListener('click', () => {
    errorDialog.remove()
  })

  errorDialog.append(heading, message, closeButton)
  errorDialog.style.cssText = `
    transform: translate(-50%, -50%);
    padding: 20px;
    background-color: #fff;
    border: 1px solid #000;
    z-index: 1000;
    max-width: 600px;
    margin: 0;
    transform: translate(-50%, 100%);
    left: 50%;
  `
  const wrapperElement = document.createElement('div')
  wrapperElement.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
  `
  wrapperElement.appendChild(errorDialog)

  return wrapperElement
}

/**
 * Create widget element based on widget type
 * @param widgetType - The type of widget to create
 * @returns HTMLElement - The created widget element
 */
export function createWidgetElement(widgetType: string): HTMLElement {
  switch (widgetType) {
    case 'storyblock':
    case 'floating_player':
      return document.createElement('fw-storyblock')
    case 'player_deck':
      return document.createElement('fw-player-deck')
    case 'carousel':
    case 'carousel_grid':
    case 'dynamic_carousel':
    case 'circle_stories':
    case 'vertical_stories':
      return document.createElement('fw-embed-feed')
    default:
      return document.createElement('fw-embed-feed')
  }
}

/**
 * Get widget mode based on widget type
 * @param widgetType - The type of widget
 * @returns string | null - The mode for the widget
 */
export function getWidgetMode(widgetType: string): string | null {
  switch (widgetType) {
    case 'carousel':
      return 'row'
    case 'carousel_grid':
      return 'grid'
    case 'floating_player':
      return 'pinned'
    default:
      return null
  }
}

/**
 * Check if section already exists for the given key
 * @param key - The section key
 * @returns boolean - True if section exists, false otherwise
 */
export function hasSectionBeenAdded(key: string): boolean {
  const sectionKey = `auto-embed-section-${key}`
  return document.querySelectorAll(`.${sectionKey}`).length > 0
}

/**
 * Create container element for the widget
 * @param key - The section key
 * @param widgetType - The type of widget
 * @returns HTMLElement - The created container element
 */
export function createContainerElement(
  key: string,
  widgetType: string,
): HTMLElement {
  const sectionKey = `auto-embed-section-${key}`
  const container = document.createElement('section')
  container.className = `shopify-section section ${sectionKey}`
  container.setAttribute(
    'style',
    `margin: ${widgetType === 'floating_player' ? '0' : '50px'} auto; height: ${widgetType === 'storyblock' ? '75vh' : ''};`,
  )
  return container
}

/**
 * Configure common widget attributes
 * @param node - The widget element to configure
 * @param embedConfig - The embed configuration
 * @param widgetId - The widget ID
 */
export function configureCommonWidgetAttributes(
  node: HTMLElement,
  embedConfig: IAutoEmbedConfig,
  widgetId: string,
): void {
  const { channel_name, widget_properties, title, widget_type } = embedConfig

  node.setAttribute('id', widgetId)
  node.setAttribute('channel', channel_name)
  node.setAttribute('size', widget_properties?.size || 'large')
  node.setAttribute('open_in', 'default')
  node.setAttribute(
    'widget_source',
    widget_properties?.widget_source || 'auto_embed_shopify',
  )
  if (widget_type === 'storyblock') {
    node.setAttribute('max_videos', '5')
  }
  node.setAttribute('title', title ?? '')
  node.setAttribute('branding', `${widget_properties.branding}`)
  node.setAttribute(
    'title_alignment',
    widget_properties.title_alignment || 'center',
  )
  node.setAttribute('auto_embed_widget', 'true')

  const mode = getWidgetMode(widget_type)
  if (mode) {
    node.setAttribute('mode', mode)
  }

  if (widget_type === 'dynamic_carousel') {
    node.setAttribute('thumbnail_style', 'dynamic')
  }

  if (widget_type === 'circle_stories') {
    node.setAttribute('thumbnail_shape', 'circle')
    node.setAttribute('product_tag_placement', 'false')
  }

  if (widget_type === 'vertical_stories') {
    node.setAttribute('thumbnail_shape', 'rectangle')
    node.setAttribute('product_tag_placement', 'false')
  }
}

/**
 * Insert widget container after target element
 * @param container - The container element to insert
 * @param siblingSelector - The selector for the sibling element
 * @returns boolean - True if insertion was successful, false otherwise
 */
export function insertWidgetContainer(
  container: HTMLElement,
  siblingSelector: string,
): boolean {
  const selector1 = siblingSelector
  const selector2 = 'main .section-main-product'

  const targetElement =
    document.querySelector(selector1) || document.querySelector(selector2)

  if (targetElement && targetElement.parentNode) {
    targetElement.parentNode.insertBefore(container, targetElement.nextSibling)
    return true
  }

  return false
}

/**
 * Handle widget insertion with error handling
 * @param container - The container element to insert
 * @param siblingSelector - The selector for the sibling element
 */
export function handleWidgetInsertion(
  container: HTMLElement,
  siblingSelector: string,
): void {
  const insertionSuccess = insertWidgetContainer(container, siblingSelector)

  if (!insertionSuccess) {
    console.error('Target element not found')
    if (isPreviewMode()) {
      const errorDialog = createErrorDialogElement()
      document.body.appendChild(errorDialog)
    }
  }
}

/**
 * Setup auto embed for homepage
 * Fetch the embed details from the API and create the embed feed element
 * Insert the embed feed element before the target element
 * The target element is the element selected in the embed settings
 * If the target element is not found, show an error dialog in preview mode
 */
export async function setupHomePageAutoEmbed() {
  const data = await fetchEmbedDetails('shopify_standard_homepage')
  if (data) {
    for (const key in data) {
      const embedConfig = data[key]
      const { playlist_id, sibling_selector, widget_type } = embedConfig

      if (hasSectionBeenAdded(key)) {
        console.error('Section already exists for this key', key)
        return
      }

      const widgetId = `fw-embed-feed-${key}`
      const container = createContainerElement(key, widget_type)
      const node = createWidgetElement(widget_type)

      configureCommonWidgetAttributes(node, embedConfig, widgetId)
      node.setAttribute('playlist', playlist_id)

      container.appendChild(node)
      handleWidgetInsertion(container, sibling_selector)
    }
  }
}

/**
 * Setup auto embed for product page
 * Fetch the embed details from the API and create the embed feed element
 * Insert the embed feed element before the target element
 * The target element is the element selected in the embed settings
 * If the target element is not found, show an error dialog in preview mode
 */
export async function setupProductPageAutoEmbed() {
  const data = await fetchEmbedDetails('shopify_standard_product_page')
  if (data) {
    for (const key in data) {
      const embedConfig = data[key]
      const { sibling_selector, widget_properties } = embedConfig

      if (hasSectionBeenAdded(key)) {
        console.error('Section already exists for this key', key)
        return
      }

      const widgetId = `fw-embed-feed-${key}`
      const container = createContainerElement(key, embedConfig.widget_type)
      const node = createWidgetElement(embedConfig.widget_type)

      configureCommonWidgetAttributes(node, embedConfig, widgetId)
      node.setAttribute('skus', window.meta.product.id)
      node.setAttribute(
        'product_card',
        widget_properties.product_card.toString(),
      )

      container.appendChild(node)
      handleWidgetInsertion(container, sibling_selector)
    }
  }
}

/**
 * Add play icon to thumbnail image
 * @param thumbnailImg - The thumbnail image element to add play icon to
 */
function addPlayIconToThumbnail(thumbnailImg: HTMLImageElement) {
  // Create play icon element
  const playIcon = document.createElement('div')
  playIcon.classList.add('fw-video-play-icon')
  playIcon.style.cssText = `
      position: absolute;
      top: 8px;
      right: 8px;
      width: 24px;
      height: 24px;
      background-color: rgba(255, 255, 255, 0.8);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
      pointer-events: none;
    `

  // Create triangle for play icon
  playIcon.innerHTML = `
      <svg width="12" height="12" viewBox="0 0 8 12" fill="white" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 0L10 6L0 12V0Z" fill="black"/>
      </svg>
    `

  // Function to position the play icon
  const positionPlayIcon = () => {
    const rect = thumbnailImg.getBoundingClientRect()
    if (rect.width > 0) {
      // Remove any existing play icons
      const existingIcons = thumbnailImg.parentElement?.querySelectorAll(
        '.fw-video-play-icon',
      )
      existingIcons?.forEach((icon) => {
        if (icon !== playIcon) {
          icon.remove()
        }
      })

      // Position at top-right corner of the image
      playIcon.style.position = 'absolute'
      playIcon.style.left = `${rect.width - 32}px` // 32px = icon width + margin
      playIcon.style.top = '8px'

      // Make sure the icon is inside the image
      if (
        thumbnailImg.parentElement &&
        !thumbnailImg.parentElement.contains(playIcon)
      ) {
        thumbnailImg.parentElement.appendChild(playIcon)
      }
    }
  }

  // Position the icon initially
  positionPlayIcon()

  // Update position when image loads
  thumbnailImg.addEventListener('load', positionPlayIcon)

  // Use ResizeObserver to watch for size changes
  if (window.ResizeObserver) {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === thumbnailImg) {
          positionPlayIcon()
        }
      }
    })
    resizeObserver.observe(thumbnailImg)
  }

  // Also update on window resize as fallback
  window.addEventListener('resize', positionPlayIcon)
}

/**
 * Setup auto embed for product page image gallery
 * Replace the image gallery with the storyblock element
 * The image gallery alt tag should be in the format fwpdp_channel_videoId
 * Example: fwpdp_fashion_week_paris_12345
 * The channel name is fashion_week_paris and the video id is 12345
 */
export function setupProductPageImageGalleryAutoEmbed() {
  const imageGalleryElements = document.querySelectorAll(
    'img[alt^="fwpdp"]',
  ) as NodeListOf<HTMLImageElement>
  //get the element with the largest width
  let imageGalleryElement: HTMLImageElement | null = null
  let maxWidth = 0

  imageGalleryElements.forEach((img) => {
    if (!imageGalleryElement || img.width > maxWidth) {
      imageGalleryElement = img
      maxWidth = img.width
    }
  })

  if (!imageGalleryElement) {
    // eslint-disable-next-line
    console.warn('No image gallery element found')
    return
  }

  // Add play icon to thumbnail elements (smaller images)
  imageGalleryElements.forEach((img) => {
    if (img !== imageGalleryElement && img.width < maxWidth) {
      addPlayIconToThumbnail(img)
    }
  })

  const altTagArr = (imageGalleryElement as HTMLImageElement).alt.split('_')
  const { channelName, videoId } = altTagArr.reduce(
    (
      acc: { channelName: string; videoId: string },
      curr: string,
      index: number,
    ) => {
      //first element is fwpdp, drop that and not useful
      //last element is videoId,
      //all elements in between are channelName joined by _

      if (curr === 'fwpdp') {
        return acc
      }

      if (index === altTagArr.length - 1) {
        return { ...acc, videoId: curr }
      }

      return {
        ...acc,
        channelName: acc.channelName + (acc.channelName ? '_' : '') + curr,
      }
    },
    { channelName: '', videoId: '' },
  )

  // found whether the story block is already in the page, need to check the video and channel name duplicate
  const storyBlockElements = document.querySelectorAll('fw-storyblock')
  const isDuplicate = Array.from(storyBlockElements).some((storyBlock) => {
    const storyBlockChannel = storyBlock.getAttribute('channel')
    const storyBlockVideo = storyBlock.getAttribute('video')
    return storyBlockChannel === channelName && storyBlockVideo === videoId
  })
  if (isDuplicate) {
    return
  }

  const storyblockNode = document.createElement('fw-storyblock')
  storyblockNode.setAttribute('channel', channelName)
  storyblockNode.setAttribute('video', videoId)
  storyblockNode.setAttribute('size', 'large')
  storyblockNode.setAttribute('max_videos', '1')
  storyblockNode.setAttribute('branding', 'true')
  storyblockNode.setAttribute(
    'widget_presentation',
    'storyblock_product_gallery',
  )
  storyblockNode.style.cssText = `
    width: 100%;
    height: 100%;
    z-index: 99;
  `

  //Attempt to replace the image in the gallery view not the thumbnail view
  const imageElement = imageGalleryElement as HTMLImageElement
  if (imageElement instanceof HTMLImageElement) {
    storyblockNode.setAttribute(
      'data-media-id',
      imageElement.getAttribute('data-media-id') ?? '',
    )
    imageElement.replaceWith(storyblockNode)
  } else {
    console.error('imageGalleryElement is not a valid HTML element')
  }
}

/**
 * Setup auto embed
 * If the page is the homepage, setup auto embed for homepage
 * If the page is a product page, setup auto embed for product page
 * If the page type is not supported, do nothing
 */
function setupAutoEmbed() {
  const pageType = getPageType()
  if (pageType === 'shopify_standard_homepage') {
    setupHomePageAutoEmbed()
  } else if (pageType === 'shopify_standard_product_page') {
    setupProductPageImageGalleryAutoEmbed()
    setupProductPageAutoEmbed()
  } else {
    //do nothing
  }
}

const initializeAutoEmbed = once(setupAutoEmbed)

/**
 * Setup Auto embed when fwn is detected
 */
export function autoEmbed() {
  if (window['_fwn']) {
    initializeAutoEmbed()
  } else {
    document.addEventListener('fw:ready', () => {
      initializeAutoEmbed()
    })
  }
}
